import { Box, Typography, Paper, Stack, useTheme, Divider } from "@mui/material";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import HomeIcon from "@mui/icons-material/Home";
import BusinessIcon from "@mui/icons-material/Business";
import PeopleIcon from "@mui/icons-material/People";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

interface AmenitiesProps {
    amenities: Record<string, string[]>;
}

// Mapeo de iconos y colores para cada categoría
const categoryConfig: Record<string, { icon: any; bgColor: string; checkmarkColor: string; textColor: string }> = {
    Interior: {
        icon: HomeIcon,
        bgColor: "#f9f4f1",
        checkmarkColor: "#d4a574",
        textColor: "#000",
    },
    Exterior: {
        icon: BusinessIcon,
        bgColor: "#e8f5e9",
        checkmarkColor: "#4caf50",
        textColor: "#000",
    },
    Community: {
        icon: PeopleIcon,
        bgColor: "#0e2340",
        checkmarkColor: "#d4a574",
        textColor: "#fff",
    },
};

export default function Amenities({ amenities }: AmenitiesProps) {
    const theme = useTheme();

    return (
        <Paper
            elevation={2}
            sx={{
                p: { xs: 2, md: 3 },
                mt: 4,
                borderRadius: 3,
                backgroundColor: "background.paper",
                width: "100%",
            }}
        >
            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                <StarBorderIcon
                    sx={{
                        fontSize: 32,
                        color: theme.palette.primary.main,
                    }}
                />
                <Typography
                    variant="h5"
                    fontWeight="bold"
                    sx={{
                        color: theme.palette.primary.main,
                    }}
                >
                    Amenities & Features
                </Typography>
            </Box>

            <Divider sx={{ mb: 3, borderColor: '#f8f4f0', borderBottomWidth: '2px' }} />

            {/* Categories - Stacked vertically */}
            <Stack spacing={2}>
                {Object.entries(amenities).map(([title, list], i) => {
                    const config = categoryConfig[title] || {
                        icon: HomeIcon,
                        bgColor: "#f9f4f1",
                        checkmarkColor: theme.palette.secondary.main,
                        textColor: "#000",
                    };
                    const IconComponent = config.icon;

                    return (
                        <Paper
                            key={i}
                            elevation={0}
                            sx={{
                                p: 3,
                                backgroundColor: config.bgColor,
                                borderRadius: 2,
                            }}
                        >
                            {/* Section Header */}
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    mb: 1,
                                }}
                            >
                                <IconComponent
                                    sx={{
                                        fontSize: 28,
                                        color: config.checkmarkColor,
                                    }}
                                />
                                <Typography
                                    variant="h6"
                                    fontWeight="bold"
                                    sx={{
                                        color: config.textColor,
                                    }}
                                >
                                    {title === "Interior"
                                        ? "Interior Features"
                                        : title === "Exterior"
                                        ? "Exterior Features"
                                        : "Community Amenities"}
                                </Typography>
                            </Box>

                            <Divider sx={{ mb: 2, borderColor: 'rgba(176, 139, 79, 0.3)', borderBottomWidth: '1px' }} />

                            {/* Features List */}
                            <Stack spacing={1.5}>
                                {list.map((feature, j) => (
                                    <Box
                                        key={j}
                                        sx={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: 1.5,
                                        }}
                                    >
                                        <CheckCircleIcon
                                            sx={{
                                                fontSize: 20,
                                                color: config.checkmarkColor,
                                                mt: 0.25,
                                                flexShrink: 0,
                                            }}
                                        />
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: config.textColor,
                                                lineHeight: 1.6,
                                            }}
                                        >
                                            {feature}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </Paper>
                    );
                })}
            </Stack>
        </Paper>
    );
}
